/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, InjectionToken, LOCALE_ID, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';
import { CoreModule } from './@core/core.module';
import { ThemeModule } from './@theme/theme.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  NbButtonModule,
  NbCardModule,
  NbDatepickerModule,
  NbDialogModule,
  NbIconLibraries,
  NbInputModule,
  NbMenuModule,
  NbSidebarModule,
  NbSpinnerModule,
  NbToastrModule,
  NbWindowModule,
  NbSelectModule,
  NbCheckboxModule,
  NbPopoverModule,
  NbBadgeModule,
  NbCalendarModule,
  NbAutocompleteModule,
} from '@nebular/theme';
import { IconFactory } from './common/config/factories';
import { MessageDialogComponent } from './shared/components/message-dialog/message-dialog.component';
import { ConfirmationDialogComponent } from './shared/components/confirmation-dialog/confirmation-dialog.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PermissionPageGuard } from './shared/auth/permission-page.guard';
import { NgSelectModule } from '@ng-select/ng-select';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { NbMomentDateModule } from '@nebular/moment';
import {
  MsalModule,
  MsalRedirectComponent,
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  Configuration,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser'; // InteractionType added to imports
import { ConfigurationService } from './common/config/configuration.service';
import { loginRequest } from './shared/auth/auth-factories';
import { RouteResolver } from './shared/guards/route-resolver.guard';

export const WINDOW = new InjectionToken<Window>('window');

export function MSALInstanceFactory(configService: ConfigurationService): IPublicClientApplication {
  const authConfig = configService.getAuthConfig();

  const config = {
    auth: {
      clientId: authConfig.clientId,
      authority: authConfig.authority,
      knownAuthorities: [authConfig.knownAuthority],
      redirectUri: authConfig.redirectUri,
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback(logLevel: LogLevel, message: string) {
          switch (logLevel) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
      allowRedirectInIframe: true,
    },
  } as Configuration;

  return new PublicClientApplication(config);
}

export function MSALInterceptorConfigFactory(configService: ConfigurationService): MsalInterceptorConfiguration {
  const authConfig = configService.getAuthConfig();
  const scopePrefix = authConfig.scopePrefix;

  const protectedResourceMap = new Map<string, Array<string>>();
  authConfig.protectedResources.forEach(protectedResource => {
    const endpoint = protectedResource.resourceUrl + '/*';
    const scopes = protectedResource.scopes.map(a => scopePrefix + a);
    protectedResourceMap.set(endpoint, scopes);
  });

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent, MessageDialogComponent, ConfirmationDialogComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    MsalModule,

    // Nebular
    NbCardModule,
    NbSpinnerModule,
    NbSelectModule,
    NbInputModule,
    NbButtonModule,
    NbCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    NbBadgeModule,
    NbPopoverModule,
    NgSelectModule,
    NgbModule,
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    CoreModule.forRoot(),
    ThemeModule.forRoot(),
    // AuthModule.forRoot(),
    SweetAlert2Module.forRoot(),
    NbCalendarModule,
    FontAwesomeModule,
    NbAutocompleteModule,
    NbDatepickerModule.forRoot(),
    NbMomentDateModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: IconFactory,
      deps: [NbIconLibraries],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ConfigurationService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ConfigurationService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ConfigurationService,
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'GBP',
    },
    {
      provide: LOCALE_ID,
      useValue: 'en-GB',
    },
    PermissionPageGuard,
    RouteResolver,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
